import { z } from 'zod';
import {
  AVAILABILITY_SLOTS,
  AVAILABILITY_VALUES,
  LANGS,
  type AvailabilityValue,
  type Lang,
} from '../constants';
import { normalizeEmail } from '../household';
import { col, idSchema, normHeader, updatedAtSchema, type SheetRowResult } from './common';

/** Map of AVAILABILITY_SLOTS id → value; missing slots mean 'neznano'. */
export const availabilitySchema = z
  .record(z.string().max(20), z.enum(AVAILABILITY_VALUES))
  .refine((v) => Object.keys(v).every((k) => AVAILABILITY_SLOTS.some((s) => s.id === k)), {
    message: 'neznan termin razpoložljivosti',
  });

export type Availability = z.infer<typeof availabilitySchema>;

const volunteerFields = {
  name: z.string().trim().min(1).max(200),
  phone: z.string().trim().max(40),
  /**
   * Stored trimmed and lowercased, so equality on the column is equality of
   * inboxes: the shared-mailbox helper compares normalised addresses, and a
   * stored form that already matches lets the roster's own index serve the
   * lookup. The format is checked after the normalisation, hence the pipe —
   * as a chained check it would run first and refuse a padded address.
   */
  email: z.string().trim().toLowerCase().pipe(z.email()).nullable(),
  lang: z.enum(LANGS),
  // Profile fields from the signup sheet — lenient text ('' = unknown) so
  // import never fails on a new spelling.
  gender: z.string().trim().max(20),
  residence: z.string().trim().max(120),
  age: z.number().int().min(1).max(120).nullable(),
  experience: z.string().trim().max(40),
  availability: availabilitySchema,
  canMultiDay: z.boolean().nullable(),
  otherTasks: z.boolean().nullable(),
  /** Volunteer's own comments/wishes from the signup sheet. */
  wishes: z.string().trim().max(2000),
  /** Internal coordinator notes. */
  note: z.string().trim().max(2000),
};

export const volunteerInputSchema = z.object({
  ...volunteerFields,
  phone: volunteerFields.phone.default(''),
  lang: volunteerFields.lang.default('sl'),
  gender: volunteerFields.gender.default(''),
  residence: volunteerFields.residence.default(''),
  age: volunteerFields.age.default(null),
  experience: volunteerFields.experience.default(''),
  availability: volunteerFields.availability.default({}),
  canMultiDay: volunteerFields.canMultiDay.default(null),
  otherTasks: volunteerFields.otherTasks.default(null),
  wishes: volunteerFields.wishes.default(''),
  note: volunteerFields.note.default(''),
});

/**
 * Built from the defaults-free field shapes, like `externalGroupUpdateSchema`:
 * `.partial()` keeps each key's `.default()`, so a PATCH of one field would
 * parse to blanks for every other and erase the phone, the availability and
 * the rest of the profile.
 */
export const volunteerUpdateSchema = z
  .object(volunteerFields)
  .partial()
  .extend({ updatedAt: updatedAtSchema })
  // `updatedAt` alone would reach the database as an empty `set` and throw.
  .refine((v) => Object.keys(v).length > 1, { message: 'ni sprememb' });

export const volunteerSchema = volunteerInputSchema.extend({
  id: idSchema,
  active: z.boolean(),
  tagIds: z.array(idSchema),
  /**
   * Set when this row is a place in an external team rather than a volunteer
   * who signed up (SPEC §1.1J). Deliberately absent from `volunteerInputSchema`
   * — the roster's own routes must never be able to make a slot, or unmake one:
   * only the external-groups module writes these three.
   */
  externalGroupId: idSchema.nullable(),
  /** Slot number within the group; the label is derived from it. */
  externalSlotIndex: z.number().int().nullable(),
  /** The real person, when the group tells us who is coming. */
  externalPersonName: z.string(),
  /**
   * Set when the person withdrew from the event (SPEC §1.1K). Like the external
   * fields, deliberately absent from `volunteerInputSchema`: only the cancel and
   * uncancel endpoints write it, so neither PATCH nor a sheet re-import can.
   */
  cancelledAt: z.iso.datetime({ offset: true }).nullable(),
  cancelledByName: z.string(),
  cancelReason: z.string(),
  createdAt: z.iso.datetime({ offset: true }),
  updatedAt: updatedAtSchema,
});

export type VolunteerInput = z.infer<typeof volunteerInputSchema>;
export type Volunteer = z.infer<typeof volunteerSchema>;

// ---------------------------------------------------------------------------
// CSV import — the coordinators' signup Google Sheet exported as CSV.
// Header names are the sheet's Slovenian column titles; the three
// sheet-internal columns (organizacijske opombe, v sistemu, ODPOVED) are
// ignored. Matching is prefix-based and case/space tolerant so cosmetic
// header edits don't break the import.
// ---------------------------------------------------------------------------

const norm = (s: string) =>
  s
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

function normalizeAvailabilityCell(raw: string): AvailabilityValue {
  const v = norm(raw);
  if (v.startsWith('ni na voljo')) return 'ni-na-voljo';
  if (v.startsWith('na voljo, ne') || v.startsWith('delno')) return 'delno';
  if (v.startsWith('na voljo')) return 'na-voljo';
  return 'neznano';
}

function normalizeLang(raw: string): Lang {
  const v = norm(raw);
  return v.startsWith('ang') || v.startsWith('en') ? 'en' : 'sl';
}

function normalizeYesNo(raw: string): boolean | null {
  const v = norm(raw);
  if (v.startsWith('da') || v === 'yes') return true;
  if (v.startsWith('ne') || v === 'no') return false;
  return null;
}

/** Parse one row of the signup-sheet CSV into VolunteerInput. */
export function parseSheetVolunteerRow(raw: Record<string, string>): SheetRowResult<VolunteerInput> {
  const ime = col(raw, 'ime');
  const priimek = col(raw, 'priimek');
  const name = `${ime} ${priimek}`.trim();
  if (!name) {
    const hasAny = Object.entries(raw).some(
      ([k, v]) => !['v sistemu', 'odpoved'].includes(normHeader(k)) && (v ?? '').trim() !== '',
    );
    return hasAny ? { ok: false, error: 'manjka ime' } : { ok: false, empty: true };
  }

  const emailRaw = col(raw, 'e-pošt') || col(raw, 'e-post') || col(raw, 'email');
  const email = normalizeEmail(emailRaw) || null;
  if (email !== null && !z.email().safeParse(email).success) {
    return { ok: false, error: `neveljaven e-naslov »${email}«` };
  }

  const ageRaw = col(raw, 'starost');
  const age = /^\d{1,3}$/.test(ageRaw) ? Number(ageRaw) : null;

  const availability: Availability = {};
  for (const slot of AVAILABILITY_SLOTS) {
    const cell = col(raw, `${slot.sheetDay} ${slot.name}`);
    const value = normalizeAvailabilityCell(cell);
    if (value !== 'neznano') availability[slot.id] = value;
  }

  const parsed = volunteerInputSchema.safeParse({
    name,
    phone: col(raw, 'telefon').slice(0, 40),
    email,
    lang: normalizeLang(col(raw, 'jezik')),
    gender: col(raw, 'spol').slice(0, 20),
    residence: col(raw, 'kraj').slice(0, 120),
    age,
    experience: col(raw, 'ocenjena izkušenost').slice(0, 40),
    availability,
    canMultiDay: normalizeYesNo(col(raw, 'lahko pomaga večkrat')),
    otherTasks: normalizeYesNo(col(raw, 'predvidene tudi druge naloge')),
    wishes: col(raw, 'morebitni pomembni komentarji').slice(0, 2000),
    // The organisers' own column. Seeds the coordinator note on a new
    // volunteer; on an existing one the app's note is theirs to keep.
    note: col(raw, 'organizacijske opombe').slice(0, 2000),
  });
  if (!parsed.success) {
    return { ok: false, error: parsed.error.issues.map((i) => i.message).join('; ') };
  }
  return { ok: true, data: parsed.data };
}
